import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TIER_META } from '../constants';
import { getLocalizedBreakoutLabel } from '../utils/labels';

export function usePreparationFormatters(metadata) {
  const { t, i18n } = useTranslation();
  const numberLocale = i18n.resolvedLanguage?.startsWith('vi') ? 'vi-VN' : 'en-US';

  const fmtNum = useCallback((v, d = 0) => {
    if (v == null) return '—';
    return Number(v).toLocaleString(numberLocale, {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    });
  }, [numberLocale]);

  const fmtPct = useCallback((v) => {
    if (v == null) return '—';
    return new Intl.NumberFormat(numberLocale, {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);
  }, [numberLocale]);

  const fmtPercentUnits = useCallback((v, digits = 1, sign = false) => {
    if (v == null || Number.isNaN(Number(v))) return '—';
    return `${new Intl.NumberFormat(numberLocale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      signDisplay: sign ? 'always' : 'auto',
    }).format(Number(v))}%`;
  }, [numberLocale]);

  const fmtMultiplier = useCallback((v, digits = 1) => {
    if (v == null || Number.isNaN(Number(v))) return '—';
    return `${new Intl.NumberFormat(numberLocale, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(Number(v))}x`;
  }, [numberLocale]);

  const getSignalLabel = useCallback((signal) => {
    if (!signal) return '—';
    const entry = (metadata?.signals || []).find((item) => item?.label?.code === signal);
    return getLocalizedBreakoutLabel(entry?.label, i18n.resolvedLanguage);
  }, [i18n.resolvedLanguage, metadata]);

  const getRegimeLabel = useCallback((regime) => {
    if (!regime) return '—';
    const entry = (metadata?.market_regimes || []).find((item) => item?.code === regime);
    return getLocalizedBreakoutLabel(entry, i18n.resolvedLanguage);
  }, [i18n.resolvedLanguage, metadata]);

  const getTriggerLabel = useCallback((triggerType, triggerLabel) => {
    if (!triggerType || triggerType === 'NONE') return t('preparation.table.noTrigger');
    return getLocalizedBreakoutLabel(triggerLabel, i18n.resolvedLanguage);
  }, [i18n.resolvedLanguage, t]);

  const getSectorLabel = useCallback((sector) => {
    if (!sector) return '—';
    if (typeof sector === 'string') return sector;
    return sector[i18n.resolvedLanguage?.startsWith('vi') ? 'vi' : 'en'] || sector.vi || sector.en || sector.code || '—';
  }, [i18n.resolvedLanguage]);

  const localizeReason = useCallback(
    (reason) => getLocalizedBreakoutLabel(reason, i18n.resolvedLanguage),
    [i18n.resolvedLanguage],
  );

  const getSignalHint = useCallback((signal) => {
    const code = (signal || '').toUpperCase();
    if (code === 'BUY_PREP') return t('preparation.signalHints.buyPrep');
    if (code === 'BUY_CAUTION') return t('preparation.signalHints.buyCaution');
    if (code === 'BUY_WATCH') return t('preparation.signalHints.buyWatch');
    if (code === 'SKIP') return t('preparation.signalHints.skip');
    return null;
  }, [t]);

  const getTier = useCallback((value) => {
    if (value == null || Number.isNaN(Number(value))) return null;
    const v = Number(value);
    if (v >= 85) return 'veryStrong';
    if (v >= 70) return 'strong';
    if (v >= 55) return 'fair';
    if (v >= 40) return 'average';
    return 'weak';
  }, []);

  const getPillarVerdict = useCallback((pillarKey, value) => {
    const tier = getTier(value);
    if (!tier) return { tier: null, label: '—', hint: '', meta: TIER_META.weak };
    return {
      tier,
      label: t(`preparation.detail.pillars.tiers.${tier}`),
      hint: t(`preparation.detail.pillars.hints.${pillarKey}.${tier}`),
      meta: TIER_META[tier],
    };
  }, [getTier, t]);

  return useMemo(() => ({
    i18n,
    t,
    numberLocale,
    fmtNum,
    fmtPct,
    fmtPercentUnits,
    fmtMultiplier,
    getSignalLabel,
    getRegimeLabel,
    getTriggerLabel,
    getSectorLabel,
    localizeReason,
    getSignalHint,
    getTier,
    getPillarVerdict,
  }), [
    i18n,
    t,
    numberLocale,
    fmtNum,
    fmtPct,
    fmtPercentUnits,
    fmtMultiplier,
    getSignalLabel,
    getRegimeLabel,
    getTriggerLabel,
    getSectorLabel,
    localizeReason,
    getSignalHint,
    getTier,
    getPillarVerdict,
  ]);
}
